import EditIcon from "@mui/icons-material/EditOutlined";
import { IconButton, Stack, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import type { MetricExtensionConfiguration } from "@entrilia/shared/api/portfolioMonitoringTypes";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import type { Metric } from "../../../../api/types/portfolioMonitoringTypes";
import MetricDataTypeIcon from "./MetricDataTypeIcon";
import { useMetricsPageContext } from "./MetricsPageContext";

const ActionsCell = ({ row }: { row: Metric }) => {
  const { hasEditPermissions, onEditMetric } = useMetricsPageContext();

  if (!hasEditPermissions) {
    return null;
  }

  return (
    <IconButton size="small" onClick={() => onEditMetric(row)}>
      <EditIcon />
    </IconButton>
  );
};

const MetricNameCell = ({ row }: { row: Metric }) => {
  const { hasEditPermissions, onEditMetric } = useMetricsPageContext();

  const onClick = hasEditPermissions ? () => onEditMetric(row) : undefined;

  return (
    <Tooltip arrow title={row.description} enterDelay={500} enterNextDelay={500}>
      <Stack
        direction="row"
        spacing={1}
        onClick={onClick}
        sx={[
          { width: "100%" },
          !!onClick && {
            cursor: "pointer",
            "&:hover .MuiTypography-root": { color: "primary.main", textDecoration: "underline" },
          },
        ]}
      >
        <MetricDataTypeIcon dataType={row.dataType} color="action" />
        <TypographyTooltipEllipsis text={row.name} typographyProps={{ sx: { pl: 0.5 } }} />
      </Stack>
    </Tooltip>
  );
};

const columnDefinitions: GridColDef<Metric>[] = [
  {
    field: "name",
    headerName: "Name",
    flex: 2,
    minWidth: 160,
    renderCell: ({ row }) => <MetricNameCell row={row} />,
  },
  {
    field: "entryNo",
    headerName: "No.",
    flex: 1,
    minWidth: 112,
  },
  {
    field: "dataType",
    headerName: "Type",
    flex: 1,
    minWidth: 112,
    valueFormatter: (value: string) => autoFormatCamelCase(value),
  },
  {
    field: "category",
    headerName: "Category",
    flex: 2,
    minWidth: 160,
  },
  {
    field: "extensionConfiguration",
    headerName: "Extension",
    flex: 1.5,
    minWidth: 160,
    valueFormatter: (value: MetricExtensionConfiguration) => (value?.extensionName ? value.extensionName : ""),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 96,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => <ActionsCell row={row} />,
  },
];

interface Props {
  metrics: Metric[];
  isLoading: boolean;
}

const MetricsGrid = ({ metrics, isLoading }: Props) => {
  return (
    <DataGrid<Metric>
      rows={metrics}
      columns={columnDefinitions}
      loading={isLoading}
      noRowsText="No metrics"
      disableColumnReorder
      disableColumnSorting
    />
  );
};

export default MetricsGrid;
